import { useRouter, type ErrorComponentProps } from "@tanstack/react-router"

import Link from "@/components/link"
import { ErrorPanel } from "@anysola/ui/error-panel"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { MonitorClientError } from "@/components/monitor-client-error"
import { Button } from "@anysola/ui/button"
import { siteConfig } from "@/lib/site"

/** What the router caught, as the `Error` the error reporting reads. */
export function asError(error: unknown): Error & { digest?: string } {
  return error instanceof Error ? error : new Error(String(error))
}

/** Next's error digest, when the error carries one. */
export function digestOf(error: unknown) {
  const digest = (error as { digest?: unknown } | null)?.digest
  return typeof digest === "string" ? digest : undefined
}

/**
 * "Try again" for an error boundary: `reset` alone clears the boundary, but a
 * failed loader's match keeps its error and throws it again; reading the
 * failed routes again is what retries it.
 */
export function useRetry(reset: ErrorComponentProps["reset"]) {
  const router = useRouter()
  return () => {
    reset()
    void router.invalidate({ filter: (match) => match.status === "error" })
  }
}

/**
 * The boundary for public pages without their own (pricing, FAQ, the legal
 * pages, the home page): `app/(site)/error.tsx`. Without it an error there
 * went straight to the global one, which replaces the whole document — no
 * header, no footer, no way back but the one button it drew.
 */
export function SiteError({ error, reset }: ErrorComponentProps) {
  const retry = useRetry(reset)
  return (
    <SiteShell>
      <MonitorClientError error={asError(error)} scope="public" />
      <Container className="pt-10 pb-14 sm:pt-12 sm:pb-16">
        <ErrorPanel
          title="This page couldn’t be loaded."
          description={`Or email ${siteConfig.contactEmail}.`}
          digest={digestOf(error)}
        >
          <Button onClick={retry}>Try again</Button>
          <Button variant="secondary" render={<Link href="/" />}>
            Back to home
          </Button>
        </ErrorPanel>
      </Container>
    </SiteShell>
  )
}
